import type { ClientListItem } from '@kominiarz/shared';
import { MapPin, Phone, Plus, Search, UserRoundPlus } from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { useClientSearch, useDebounced } from '../lib/clientsApi';
import styles from './Combobox.module.css';
import { describedBy, Field } from './form/Field';

interface ClientPickerProps {
  label: string;
  hint?: string;
  error?: string;
  onSelect: (client: ClientListItem) => void;
  /** "Add new client" with whatever was typed so far. */
  onCreate: (query: string) => void;
}

const MIN_CHARS = 2;

/**
 * Search-as-you-type combobox over clients: name, phone or street. The last option
 * always offers adding a new client without leaving the form.
 */
export function ClientPicker({ label, hint, error, onSelect, onCreate }: ClientPickerProps) {
  const id = useId();
  const listId = `${id}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debounced = useDebounced(query.trim());
  const searching = debounced.length >= MIN_CHARS;
  const results = useClientSearch(debounced, { enabled: searching, limit: 8 });

  const items = searching ? (results.data?.clients ?? []) : [];
  const optionCount = items.length + 1; // + "add new"
  const showList = open && query.trim().length >= MIN_CHARS;

  function choose(index: number) {
    if (index < items.length) {
      onSelect(items[index]!);
      setQuery('');
      setOpen(false);
    } else {
      onCreate(query.trim());
      setOpen(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % optionCount);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + optionCount) % optionCount);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(active);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <Field id={id} label={label} hint={hint} error={error}>
      <div className={styles.wrap}>
        <Search aria-hidden="true" className={styles.searchIcon} />
        <input
          ref={inputRef}
          id={id}
          className={styles.input}
          type="search"
          role="combobox"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          placeholder="Szukaj klienta"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList ? `${listId}-${active}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
        />
        {showList && (
          <ul id={listId} role="listbox" className={styles.list} aria-label="Pasujący klienci">
            {searching && results.isFetching && items.length === 0 && <li className={styles.status}>Szukam…</li>}
            {searching && !results.isFetching && items.length === 0 && (
              <li className={styles.status}>Nie ma takiego klienta w bazie.</li>
            )}
            {items.map((c, i) => (
              <li
                key={c.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? `${styles.option} ${styles.active}` : styles.option}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(i)}
              >
                <span className={styles.name}>{c.name ?? c.phones[0]?.phone}</span>
                {c.phones[0] && (
                  <span className={styles.meta}>
                    <Phone aria-hidden="true" />
                    {c.phones.map((p) => p.phone).join(', ')}
                  </span>
                )}
                {c.objects[0] && (
                  <span className={styles.meta}>
                    <MapPin aria-hidden="true" />
                    {c.objects[0].address}
                    {c.objects.length > 1 ? ` (+${c.objects.length - 1})` : ''}
                  </span>
                )}
              </li>
            ))}
            <li
              id={`${listId}-${items.length}`}
              role="option"
              aria-selected={active === items.length}
              className={active === items.length ? `${styles.option} ${styles.addNew} ${styles.active}` : `${styles.option} ${styles.addNew}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(items.length)}
            >
              <span className={styles.name}>
                <UserRoundPlus aria-hidden="true" />
                Dodaj nowego klienta
              </span>
            </li>
          </ul>
        )}
      </div>
      <button type="button" className={styles.newButton} onClick={() => onCreate(query.trim())}>
        <Plus aria-hidden="true" />
        Nowy klient
      </button>
    </Field>
  );
}
